import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react'
import useDocumentComparisonData from '@/components/validation/useDocumentComparisonData'

type DocumentComparisonDataProviderValue = ReturnType<
  typeof useDocumentComparisonData
> & {
  compareDocumentEnabled: boolean
  documentIdToCompare: string
  setCompareDocumentEnabled: Dispatch<SetStateAction<boolean>>
  setDocumentIdToCompare: Dispatch<SetStateAction<string>>
  baseFieldHeights: Record<string, [string, number][]>
  setBaseFieldHeight: (fieldId: string, docId: string, height: number) => void
}

const DocumentComparisonDataContext =
  createContext<DocumentComparisonDataProviderValue>(
    {} as DocumentComparisonDataProviderValue,
  )

export const useDocumentComparisonDataContext = () =>
  useContext(DocumentComparisonDataContext)

type DocumentComparisonDataProviderProps = {
  children: ReactNode
  mainDocumentId: string
} & Omit<
  DocumentComparisonDataProviderValue,
  'baseFieldHeights' | 'setBaseFieldHeight'
>

export default function DocumentComparisonDataProvider({
  children,
  mainDocumentId,
  ...rest
}: DocumentComparisonDataProviderProps) {
  const [baseFieldHeights, setBaseFieldHeightsState] = useState<
    Record<string, [string, number][]>
  >({})

  const setBaseFieldHeight = useCallback(
    (fieldId: string, docId: string, height: number) => {
      setBaseFieldHeightsState((prev) => {
        const existing = prev[fieldId] || []
        const filtered = existing.filter(([itemDocId]) => itemDocId !== docId)
        return {
          ...prev,
          [fieldId]: [...filtered, [docId, height]],
        }
      })
    },
    [],
  )

  useEffect(() => {
    setBaseFieldHeightsState((prev) => {
      const updated = { ...prev }
      // Keep only heights for the main document if the comparison document changes
      Object.keys(updated).forEach((fieldId) => {
        updated[fieldId] = updated[fieldId].filter(
          ([docId]) => docId === mainDocumentId,
        )
        if (updated[fieldId].length === 0) {
          delete updated[fieldId]
        }
      })
      return updated
    })
  }, [mainDocumentId, rest.documentIdToCompare])

  return (
    <DocumentComparisonDataContext.Provider
      value={{
        ...rest,
        baseFieldHeights,
        setBaseFieldHeight,
      }}
    >
      {children}
    </DocumentComparisonDataContext.Provider>
  )
}
